import { useRef } from 'react';
import { useAppSelector } from '../app/hooks';
import { selectAwaitingResponseSinceMs } from '../selectors/message';

/**
 * CLIENT-CLOCK anchor for the customer utterance awaiting an agent reply.
 *
 * `selectAwaitingResponseSinceMs` is the ORDERBOARD host's own stamp on the
 * customer's final. Subtracting it from `Date.now()` — the agent's browser clock
 * — puts the agent machine's clock skew straight into the reading, which is
 * HITL-1569: on session `493089b1-…` the agent's browser ran 8.67 s fast, so the
 * Response time meter read ~9 s (red, over limit) on all 20 turns of an order
 * whose real waits were 0.64–5.05 s, and 14 `agent_metric` events fired
 * `EXCEEDED` the instant each utterance landed. Not one turn had breached.
 *
 * HITL-1513 already ruled this out for `ORDER_PROCESSING_METRIC` and added
 * `client_clock_skew_ms` precisely to spot machines that are seconds out; the
 * rule is the same here, and `useFinalOrderElapsed` is the pattern — keep both
 * ends of a duration on one clock.
 *
 * Returns `Date.now()` from the render in which this utterance became the one
 * awaiting a reply, so anything built on it measures browser-clock against
 * browser-clock. The frame's in-flight time (6–7 ms on the bus, plus network) is
 * not counted, which is right: the agent cannot answer a line before it arrives.
 *
 * Consequence worth knowing: when the agent intercepts part-way through a turn
 * the meter starts at 0 rather than at the utterance's age, so they get the full
 * limit from the moment the tile can be seen. Timing them against a line the AI
 * was still handling — and that they had no chance to answer — is what the old
 * anchor did.
 *
 * `active` is the caller's own visibility/eligibility gate. While it is false, or
 * while nothing is awaiting a reply, this returns `null` and the next arming
 * takes a fresh stamp.
 *
 * The ref is written during render because the value is derived purely from the
 * current `sinceMs`; deferring it to an effect would leave one paint pairing a
 * new utterance with the previous stamp.
 */
const useAwaitingResponseAnchor = (active: boolean): number | null => {
  const sinceMs = useAppSelector(selectAwaitingResponseSinceMs);
  const anchorRef = useRef<{ sinceMs: number | null; clientMs: number | null }>(
    { sinceMs: null, clientMs: null }
  );

  if (!active || sinceMs == null) {
    anchorRef.current = { sinceMs: null, clientMs: null };
  } else if (anchorRef.current.sinceMs !== sinceMs) {
    anchorRef.current = { sinceMs, clientMs: Date.now() };
  }

  return anchorRef.current.clientMs;
};

export default useAwaitingResponseAnchor;
